// web/src/components/SourceSlot.tsx
// Task 5 (create-screen redesign) + Figma re-skin (node 2001:2 "roast" /
// 2007:838 "toast"): Drive-first import + upload, auto-detecting deck/doc/
// photo, styled as the frosted "my deck" glass card. Reuses the pick ->
// validate -> cover pattern from StudioCreate.tsx's onPickPdf (validatePdf ->
// renderCover -> object URL).
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { AnimatePresence, motion, useAnimationControls } from "motion/react";
import { Plus, X } from "lucide-react";
import { validatePdf, renderCover } from "@/lib/pdf";
import { importDriveAsPdf, slidesImportConfigured } from "@/lib/googleSlides";
import { useAuth } from "@/contexts/AuthContext";
import { mintRoastId } from "@/lib/ids";
import { playPop, playAnimeWow, playQuack } from "@/lib/sfx";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import SpotlightCard from "@/components/SpotlightCard";
import Gleam from "@/components/Gleam";
import type { DetectedSourceType } from "@/lib/createFlow";
import type { AspectRatio, Mode } from "@/types/roast";

export interface LoadedSource {
  file: File;
  detectedType: DetectedSourceType;
  pageCount: number;
  coverUrl: string;
  coverBlob: Blob;
  aspect: AspectRatio;
  roastId: string;
}

/** Mode-tinted glass-card background — exact Figma "my deck" card gradient
 *  (a white top-sheen over a mode-colored tint: rose for roast, sky for toast). */
const CARD_TINT: Record<Mode, string> = {
  roast: "rgba(255,151,151,0.29)",
  toast: "rgba(151,203,255,0.29)",
};

function cardBackground(mode: Mode): string {
  return `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 100%), linear-gradient(90deg, ${CARD_TINT[mode]} 0%, ${CARD_TINT[mode]} 100%)`;
}

/** Mode-tinted spotlight color for the "my deck" card's hover glow. */
const SPOTLIGHT_COLOR: Record<Mode, string> = {
  roast: "rgba(255,150,120,0.6)",
  toast: "rgba(120,180,255,0.6)",
};

const CARD_CLASS =
  "w-full max-w-sm rounded-[40px] border border-white/10 p-6 shadow-[0_8px_30px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-7";

/** Punny status lines cycled while a picked Drive file is exporting + rendering. */
const IMPORT_PUNS = [
  "Preheating the oven…",
  "Marinating your deck…",
  "Basting the bullet points…",
  "Slicing the slides…",
  "Seasoning the subtext…",
  "Turning up the heat…",
  "Plating your roast…",
  "Almost well-done…",
];

/** Funny cycling progress shown while we wait for a Drive import to land in the
 *  UI (export → download → cover render). A new pun every 2 seconds. */
function ImportProgress(): JSX.Element {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % IMPORT_PUNS.length), 2000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex flex-col items-center gap-4 py-1 text-white" role="status" aria-live="polite">
      <div className="h-1.5 w-48 overflow-hidden rounded-full bg-white/15">
        <motion.div
          className="h-full w-1/3 rounded-full bg-white/80"
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.15, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25 }}
          className="text-base font-semibold"
        >
          {IMPORT_PUNS[i]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

/** decks render 16:9; docs and photos render 9:16. */
function aspectFor(type: DetectedSourceType): AspectRatio {
  return type === "deck" ? "16:9" : "9:16";
}

/** A landscape-ish first page (>= 1.2) reads as a deck; otherwise a doc. */
function detectPdfType(width: number, height: number): "deck" | "doc" {
  return width / height >= 1.2 ? "deck" : "doc";
}

export function SourceSlot({
  onLoaded,
  mode = "roast",
}: {
  /** Called with the loaded source, or `null` when it's removed (so the parent
   *  clears its state and disables Generate). */
  onLoaded: (s: LoadedSource | null) => void;
  /** Drives the glass card's tint (rose/roast vs sky/toast); purely visual. */
  mode?: Mode;
}): JSX.Element {
  const { toast } = useToast();
  const { user } = useAuth(); // Drive consent must target the SAME account the app is signed into
  const [loaded, setLoaded] = useState<LoadedSource | null>(null);
  const [validating, setValidating] = useState(false);
  // True from the moment a Drive file is picked until it lands in the UI
  // (export → download → cover render) — drives the punny progress meter.
  const [importing, setImporting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Spring "poke" the whole card when an import button is clicked.
  const cardControls = useAnimationControls();
  const pokeCard = () => {
    cardControls.set({ scale: 0.95 });
    void cardControls.start({ scale: 1 }, { type: "spring", stiffness: 460, damping: 12 });
  };

  // Revoke the cover/preview object URL on change (revokes the OLD value) and on unmount.
  useEffect(() => {
    const url = loaded?.coverUrl ?? null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [loaded?.coverUrl]);

  const emit = (s: LoadedSource) => {
    setLoaded(s);
    onLoaded(s);
    playPop(); // pop + quiet "wow" as the thumbnail springs in
    playAnimeWow();
  };

  /** validatePdf -> renderCover -> object URL, mirroring StudioCreate's onPickPdf. */
  const loadPdf = async (file: File, forcedType?: "deck" | "doc") => {
    setValidating(true);
    try {
      const res = await validatePdf(file);
      if (!res.ok) {
        toast({ title: "Couldn't read that PDF", description: res.reason, variant: "destructive" });
        return;
      }
      const cover = await renderCover(file);
      const detectedType: DetectedSourceType = forcedType ?? detectPdfType(cover.width, cover.height);
      emit({
        file,
        detectedType,
        pageCount: res.pageCount ?? 0,
        coverUrl: URL.createObjectURL(cover.blob),
        coverBlob: cover.blob,
        aspect: aspectFor(detectedType),
        roastId: mintRoastId(),
      });
    } catch (e) {
      toast({
        title: "Couldn't read that PDF",
        description: e instanceof Error ? e.message : "Try a different export.",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  const loadImage = (file: File) => {
    emit({
      file,
      detectedType: "photo",
      pageCount: 0,
      coverUrl: URL.createObjectURL(file),
      coverBlob: file,
      aspect: "9:16",
      roastId: mintRoastId(),
    });
  };

  const onPickFile = (f: File | null) => {
    if (!f) return;
    // Route by mime OR extension: iOS Files frequently hands over PDFs with an EMPTY
    // file.type, which used to fall through to the image path and fail silently
    // (broken cover, no error). Unknown types get a visible toast instead.
    if (f.type === "application/pdf" || /\.pdf$/i.test(f.name)) {
      void loadPdf(f);
    } else if (f.type.startsWith("image/")) {
      loadImage(f);
    } else {
      toast({
        title: "That file type isn't supported",
        description: "Pick a photo or a PDF (export your deck/doc to PDF first).",
        variant: "destructive",
      });
    }
  };

  const onImportDrive = async () => {
    try {
      const res = await importDriveAsPdf(() => setImporting(true), user?.email ?? undefined);
      if (!res) return; // user cancelled — quiet no-op
      await loadPdf(res.file); // auto-detect deck vs doc from the PDF's page aspect
    } catch (e) {
      toast({
        title: "Couldn't import from Google Drive",
        description: e instanceof Error ? e.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const onRemove = () => {
    playQuack(); // quack as the thumbnail springs back out
    setLoaded(null);
    onLoaded(null); // also clear the parent's source so Generate is disabled again
    if (fileInput.current) fileInput.current.value = "";
  };

  /* Deck<->doc override — commented out for now (kept for later). Re-enable this
     plus the "Detected / Switch to …" UI in the loaded state below to bring it back.
  const toggleDetected = () => {
    if (!loaded || loaded.detectedType === "photo") return;
    const flipped: DetectedSourceType = loaded.detectedType === "deck" ? "doc" : "deck";
    emit({ ...loaded, detectedType: flipped, aspect: aspectFor(flipped) });
  };
  */

  // One shared spring for the card <-> thumbnail morph.
  const morph = { type: "spring", stiffness: 260, damping: 20 } as const;

  return (
    <div className="flex w-full flex-col items-center">
      {/* popLayout so the outgoing state is pulled out of flow and the incoming
          one springs into its place — a seamless tween between the buttons card
          and the tilted thumbnail (and the reverse on remove). initial={false} so
          the first paint doesn't animate in. */}
      <AnimatePresence initial={false} mode="popLayout">
        {importing && (
          <motion.div
            key="importing"
            className={CARD_CLASS}
            style={{ backgroundImage: cardBackground(mode) }}
            data-testid="source-importing"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={morph}
          >
            <ImportProgress />
          </motion.div>
        )}

        {!importing && !loaded && (
          <motion.div
            key="empty"
            className="w-full max-w-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={morph}
          >
            <motion.div animate={cardControls}>
              <SpotlightCard
                className={CARD_CLASS}
                spotlightColor={SPOTLIGHT_COLOR[mode]}
                style={{ backgroundImage: cardBackground(mode) }}
              >
                {/* Card title (decorative) — the two buttons below are the actions. */}
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <Plus className="h-6 w-6 shrink-0 text-white" aria-hidden="true" />
                  <img
                    src="/icons/google-slides.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-10 w-8 shrink-0 sm:h-12 sm:w-9"
                  />
                  <span className="text-xl font-bold text-white sm:text-2xl">my deck</span>
                </div>

                <div className="mt-5 space-y-2">
                  {slidesImportConfigured() && (
                    <Button
                      type="button"
                      onClick={() => { pokeCard(); onImportDrive(); }}
                      disabled={validating}
                      className="w-full border border-white/15 bg-white/15 text-white shadow-none hover:bg-white/25"
                    >
                      Google Drive
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { pokeCard(); fileInput.current?.click(); }}
                    disabled={validating}
                    className="w-full border border-white/10 bg-white/10 text-white shadow-none hover:bg-white/20"
                  >
                    {/* Device-appropriate label via the pointer signal (not screen width):
                        touch devices read "My Phone", fine pointers "My Computer". */}
                    <span className="[@media(pointer:coarse)]:hidden">My Computer</span>
                    <span className="hidden [@media(pointer:coarse)]:inline">My Phone</span>
                  </Button>
                  <input
                    id="source-upload"
                    ref={fileInput}
                    type="file"
                    accept=".pdf,application/pdf,image/*"
                    disabled={validating}
                    aria-label="Upload a file"
                    onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                    className="sr-only"
                  />
                </div>
                {validating && <p className="mt-3 text-center text-sm text-white/70">Reading your file…</p>}
              </SpotlightCard>
            </motion.div>
          </motion.div>
        )}

        {!importing && loaded && (
          <motion.div
            key="loaded"
            data-testid="source-loaded"
            // Appear + rotate into the 5° tilt on add; reverse (untilt + shrink) on remove.
            initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
            animate={{ opacity: 1, scale: 1, rotate: 5 }}
            exit={{ opacity: 0, scale: 0.8, rotate: 0 }}
            transition={morph}
          >
            {/* The cover floats like a physical sheet: gently bobbing (sine) on a soft
                drop shadow. The tilt/entrance is on the wrapper above; the bob is here. */}
            <motion.div
              className="relative"
              initial={{ y: 0 }}
              animate={{ y: -12 }}
              transition={{ y: { duration: 1.1, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } }}
            >
              <img
                src={loaded.coverUrl}
                alt={`${loaded.detectedType} cover`}
                className="block max-h-64 max-w-[20rem] rounded-md shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)]"
                style={{ viewTransitionName: "source-thumb" }}
              />
              {/* Gleam sweeps across once as the thumbnail settles in. */}
              <Gleam key={loaded.roastId} className="rounded-md" />
              {/* Upper-right close button removes the file → back to the add state. */}
              <button
                type="button"
                onClick={onRemove}
                aria-label="Remove file"
                className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-black"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
